import type { ListingItem } from "@/hooks/use-paginated-feed";
import type { FeedRedesignListing, MockFeedItemKind } from "@/components/feed/redesign/mockListings";

export type DiscoverMockListing = ListingItem & {
  discoverTypeLabel: string;
  recommendedRank: number;
};

const TYPE_LABELS: Record<MockFeedItemKind, string> = {
  buy_now: "Buy now",
  auction: "Auction",
  offers: "Offers",
  pre_order: "Pre-order",
  scheduled: "Scheduled",
};

function listingFormatForType(t: MockFeedItemKind): string | null {
  if (t === "buy_now") return "fixed_price";
  if (t === "auction") return "auction";
  if (t === "offers") return "offers_only";
  if (t === "pre_order") return "fixed_price";
  if (t === "scheduled") return "fixed_price";
  return null;
}

export function feedRedesignToDiscoverListing(
  l: FeedRedesignListing,
  recommendedRank: number
): DiscoverMockListing {
  const priceNum =
    l.listing_type === "auction" && l.auction_current_bid != null
      ? l.auction_current_bid
      : (l.price ?? 0);
  const conditionRaw = l.condition ?? "good";
  const condition = conditionRaw.charAt(0).toUpperCase() + conditionRaw.slice(1).toLowerCase();
  return {
    id: l.id,
    title: l.title,
    description: l.description,
    price: String(priceNum),
    currency: l.currency,
    image_urls: l.photos,
    // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: `location` removed; mock data
    // has no real address. Orphan-listing equivalent.
    shipFromAddress: null,
    condition,
    grade: "N/A",
    created_at: l.listed_at,
    user_id: "mock-seller",
    quantity: l.stock ?? 1,
    sellerUsername: l.seller.username,
    sellerAvatarUrl: l.seller.avatar_url ?? undefined,
    initialSaved: false,
    scheduled_publish_at: l.scheduled_drops_at ?? null,
    listing_format: listingFormatForType(l.listing_type),
    auction_start_price: l.listing_type === "auction" ? l.price : null,
    auction_highest_bid: l.auction_current_bid ?? null,
    auction_end_at: l.auction_ends_at ?? null,
    auction_status: l.listing_type === "auction" ? "active" : null,
    auction_bid_count: l.listing_type === "auction" ? 3 : null,
    discoverTypeLabel: TYPE_LABELS[l.listing_type],
    recommendedRank,
  };
}

const hour = 60 * 60 * 1000;
const day = 24 * hour;
const now = Date.now();

function mk(
  id: string,
  partial: Omit<FeedRedesignListing, "id" | "seller" | "currency" | "listed_at"> &
    Partial<Pick<FeedRedesignListing, "seller" | "currency" | "listed_at">>,
  rank: number
): DiscoverMockListing {
  const seller = partial.seller ?? { username: "mockshop", avatar_url: null, sales_count: 100 };
  const row: FeedRedesignListing = {
    id,
    seller,
    currency: partial.currency ?? "USD",
    listed_at: partial.listed_at ?? new Date(now - rank * hour).toISOString(),
    title: partial.title,
    description: partial.description,
    category: partial.category,
    photos: partial.photos,
    listing_type: partial.listing_type,
    price: partial.price ?? null,
    auction_current_bid: partial.auction_current_bid,
    auction_ends_at: partial.auction_ends_at,
    preorder_ships_at: partial.preorder_ships_at,
    scheduled_drops_at: partial.scheduled_drops_at,
    condition: partial.condition,
    stock: partial.stock,
    comment_count: partial.comment_count,
  };
  return feedRedesignToDiscoverListing(row, rank);
}

function series(
  category: string,
  prefix: string,
  types: MockFeedItemKind[],
  photoSeed: string,
  rankOffset: number,
  photoMode: "picsum" | "placeholder" = "picsum",
  count = 48
): DiscoverMockListing[] {
  const titles = [
    `${category} highlight A`,
    `${category} highlight B`,
    `${category} collector pick`,
    `${category} rare lot`,
    `${category} bundle`,
    `${category} single`,
    `${category} graded`,
    `${category} sealed`,
    `${category} display`,
    `${category} vintage piece`,
    `${category} limited`,
    `${category} restock`,
  ];
  return Array.from({ length: count }, (_, i) => {
    const title = i < titles.length ? titles[i]! : `${category} listing ${i + 1}`;
    const t = types[i % types.length];
    const id = `discover-${prefix}-${i}`;
    const rank = rankOffset + i;
    const base: Omit<FeedRedesignListing, "id" | "seller" | "currency" | "listed_at" | "price"> & {
      price?: number | null;
    } = {
      title,
      description: `${title} — mock discover listing.`,
      category,
      photos: [
        photoMode === "placeholder"
          ? "/placeholder.svg"
          : `https://picsum.photos/seed/${photoSeed}${i}/800/800`,
      ],
      listing_type: t,
      condition: i % 3 === 0 ? "New" : "Good",
      stock: 1,
    };
    if (t === "auction") {
      return mk(id, {
        ...base,
        price: 40 + i * 5,
        auction_current_bid: 55 + i * 6,
        auction_ends_at: new Date(now + (i + 1) * day).toISOString(),
      }, rank);
    }
    if (t === "scheduled") {
      return mk(id, {
        ...base,
        price: null,
        scheduled_drops_at: new Date(now + (i + 2) * day).toISOString(),
      }, rank);
    }
    if (t === "pre_order") {
      return mk(id, {
        ...base,
        price: 29 + i * 3,
        preorder_ships_at: new Date(now + 14 * day).toISOString(),
        stock: 20,
      }, rank);
    }
    return mk(id, {
      ...base,
      price: 19 + i * 7,
    }, rank);
  });
}

export function buildDiscoverFallbackMockListings(brandName: string, slugPrefix: string): DiscoverMockListing[] {
  const safe = slugPrefix.replace(/[^a-z0-9]/gi, "") || "brand";
  const seed = safe.slice(0, 12);
  return series(brandName, `${safe}Fb`, ["buy_now", "auction", "offers", "pre_order"], seed, 0, "placeholder");
}

export type CategorySortMode =
  | "recommended"
  | "newest"
  | "price-asc"
  | "price-desc"
  | "ending-soon";

export function sortDiscoverListings(listings: DiscoverMockListing[], mode: CategorySortMode): DiscoverMockListing[] {
  const copy = [...listings];
  if (mode === "recommended") {
    copy.sort((a, b) => a.recommendedRank - b.recommendedRank);
    return copy;
  }
  if (mode === "newest") {
    copy.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return copy;
  }
  if (mode === "price-asc") {
    copy.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    return copy;
  }
  if (mode === "price-desc") {
    copy.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    return copy;
  }
  const auctions = copy.filter((l) => l.listing_format === "auction" && l.auction_end_at);
  const rest = copy.filter((l) => !(l.listing_format === "auction" && l.auction_end_at));
  auctions.sort(
    (a, b) => new Date(a.auction_end_at!).getTime() - new Date(b.auction_end_at!).getTime()
  );
  return [...auctions, ...rest];
}

export function categoryHasAuctions(listings: DiscoverMockListing[]): boolean {
  return listings.some((l) => l.listing_format === "auction" && l.auction_end_at);
}
